import { gql } from "@apollo/client";
import PropTypes from "prop-types";
import DOMPurify from "isomorphic-dompurify";

import airbrake from "util/airbrake";
import { initializeApollo, addApolloState } from "init/apolloClient";
import { titleize, parameterize } from "util/strings";
import PublicSearch from "components/PublicSearch";
import { SQUAREFOOT_FEATURED_LISTINGS_QUERY } from "components/SquarefootFeaturedListings";

export const AREA_REDIRECT_QUERY = gql`
  query areaRedirect($pathPart: String!) {
    areaRedirect(pathPart: $pathPart) {
      areaType
      externalId
      originalPath
      redirectPath
    }
  }
`;

export const SEARCH_PAGE_QUERY = gql`
  query publicSearchPageQuery(
    $city: String!
    $state: String
    $neighborhoods: String
    $numEmployees: String
    $offset: Int
    $limit: Int
    $slug: String!
  ) {
    listingSearch(
      city: $city
      state: $state
      neighborhoods: $neighborhoods
      numEmployees: $numEmployees
      offset: $offset
      limit: $limit
    ) {
      count
      offset
      listings {
        id
        squareFeet
        estimatedSquareFeet
        status
        floorAndSuite
        estimatedMonthlyPrice
        accurateMonthlyPrice
        displayPSF
        displayMonthlyPrice
        maxOccupancy
        estimatedMaxOccupancy
        coverImage {
          id
          url
        }
        sublease
        leaseTerm
        leaseType
        description
        estimatedPsf
        accuratePsf
        building {
          id
          identity
          lat
          lng
          zip
          address
          slug
          city
          state
          classification: buildingClassification
          neighborhood {
            id
            name
            parameterizedName
          }
          market {
            id
            name
          }
          submarket {
            id
            name
          }
          hasVirtualTours
        }
        brokerInsights {
          id
        }
      }
    }
    city(name: $city, state: $state) {
      id
      market {
        id
        name
        externalId
        seoString
        position {
          lat
          lng
        }
        neighborhoods {
          id
          name
          parameterizedName
          geojson
          externalId
        }
      }
      neighborhoods {
        id
        name
        parameterizedName
        geojson
        externalId
      }
      cmsContent {
        title
        content
        faq {
          question
          answer
        }
      }
    }
    neighborhood(slug: $slug) {
      id
      name
      geojson
      parameterizedName
      externalId
      cmsContent {
        id: title
        title
        content
        faq {
          question
          answer
        }
      }
    }
  }
`;

export default function PublicSearchPage({
  state,
  city,
  neighborhoods,
  limit,
  numEmployees,
  page,
  area,
  data,
  sanitizedCMS,
}) {
  return (
    <PublicSearch
      state={state}
      city={city}
      market={data.city?.market}
      neighborhoods={neighborhoods}
      listings={data?.listingSearch?.listings}
      count={data?.listingSearch?.count}
      cmsContent={data?.cmsContent}
      area={area}
      limit={limit}
      page={page}
      numEmployees={numEmployees}
      sanitizedCMS={sanitizedCMS}
      isOOM
    />
  );
}

const getCMSSlug = (state, city, neighborhoods) => {
  const baseSlug = `/${state}/${city}`;
  if (!neighborhoods) return baseSlug;
  return decodeURIComponent(neighborhoods).includes(",")
    ? baseSlug
    : `${baseSlug}/${parameterize(neighborhoods)}`;
};

export async function getServerSideProps({
  query,
  resolvedUrl,
  params: { slug },
}) {
  const apolloClient = initializeApollo();

  let path = resolvedUrl
    .split("?")[0]
    .replace("/office-space/", "")
    .toLowerCase();

  // this solved for mis canonicalised urls like https://www.squarefoot.com/office-space/fl/miami/Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell,Brickell
  if (path.includes(",")) [path] = path.split(",");

  const { data: areaRedirectData, error: areaRedirectError } =
    await apolloClient.query({
      query: AREA_REDIRECT_QUERY,
      variables: { pathPart: path },
    });

  if (areaRedirectError) {
    airbrake.notify({
      error: areaRedirectError,
    });
  }

  let { page = 1, numEmployees } = query;
  if (areaRedirectData?.areaRedirect?.redirectPath) {
    const [, queryParams] = resolvedUrl.split("?");

    let destination = `/office-space/m/${areaRedirectData.areaRedirect.redirectPath}/${areaRedirectData.areaRedirect.externalId}`;
    if (queryParams) destination += `?${queryParams}`;

    return {
      redirect: {
        permanent: true,
        destination,
      },
    };
  }

  let neighborhoods;

  if (slug[2] && !query.neighborhoods) {
    neighborhoods = slug[2] || undefined;
  } else if (slug[2] && query.neighborhoods) {
    neighborhoods = `${slug[2]},${query.neighborhoods}`;
  } else if (!slug[2] && query.neighborhoods) {
    neighborhoods = query.neighborhoods;
  }

  // Crawlers are hitting pages with the following query param occuring multiple times, these two checks handle that case
  if (Array.isArray(page)) [page] = page;

  if (Array.isArray(numEmployees)) [numEmployees] = numEmployees;

  page = Number.parseInt(page, 10);

  const state = slug[0] || "";
  const city = slug[1] || "";
  const limit = 12;
  const offset = (page - 1) * limit;

  const cmsSlug = getCMSSlug(state, city, neighborhoods);

  let error;
  let data;

  const searchPageQuery = apolloClient.query({
    query: SEARCH_PAGE_QUERY,
    variables: {
      state: state.toUpperCase(),
      city: titleize(city),
      neighborhoods: neighborhoods ? decodeURIComponent(neighborhoods) : null,
      offset,
      limit,
      numEmployees,
      slug: cmsSlug,
    },
  });

  // Fetch featured listings so that they're available in Apollo locally when the page renders
  const featuredListingsQuery = apolloClient.query({
    query: SQUAREFOOT_FEATURED_LISTINGS_QUERY,
    variables: {
      city: titleize(city),
      neighborhoods: neighborhoods
        ? titleize(decodeURIComponent(neighborhoods))
        : "",
      live: true,
    },
  });

  // But let's make sure we're not blocking requests to Apollo on one another.
  await Promise.all([featuredListingsQuery, searchPageQuery]).then(
    (responses) => {
      [, { error, data }] = responses;
    }
  );

  if (error) throw new Error(error);

  let cmsContent = null;

  // only show cmsContent directly related to last subdirectory
  if (neighborhoods && data?.neighborhood?.cmsContent)
    cmsContent = data?.neighborhood?.cmsContent;
  else if (
    (!neighborhoods || neighborhoods.includes(",")) &&
    data?.city?.cmsContent
  )
    cmsContent = data?.city?.cmsContent;

  let sanitizedCMS = null;

  if (cmsContent) {
    sanitizedCMS = {
      title: DOMPurify.sanitize(cmsContent.title),
      content: DOMPurify.sanitize(cmsContent.content),
      faq: cmsContent.faq,
    };
  }

  let embellishedNeighborhoods;
  const greaterAreaNeighborhoods =
    data.city?.market?.neighborhoods || data.city?.neighborhoods;
  if (greaterAreaNeighborhoods) {
    embellishedNeighborhoods = neighborhoods
      ? neighborhoods.split(",").reduce((acc, neighborhood) => {
          const found = greaterAreaNeighborhoods.find(
            (n) => n.parameterizedName === neighborhood
          );
          return found ? [found, ...acc] : acc;
        }, [])
      : [];
  } else {
    embellishedNeighborhoods = neighborhoods
      ? neighborhoods.split(",").map((n) => ({ name: titleize(n) }))
      : [];
  }

  /* if the value for number of employess is absurdly large just search for any size */
  const numEmployeesValue =
    numEmployees && parseInt(numEmployees, 10) < 100000 ? numEmployees : null;

  return addApolloState(apolloClient, {
    props: {
      state,
      city,
      sanitizedCMS,
      neighborhoods: embellishedNeighborhoods,
      offset,
      limit,
      numEmployees: numEmployeesValue,
      data: data ?? null,
      page: parseInt(page, 10),
      area: slug[2] ?? city,
      cmsSlug,
    },
  });
}

PublicSearchPage.propTypes = {
  state: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  limit: PropTypes.number.isRequired,
  numEmployees: PropTypes.string,
  page: PropTypes.number.isRequired,
  area: PropTypes.string.isRequired,
  sanitizedCMS: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
  }),
  neighborhoods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string.isRequired,
      parameterizedName: PropTypes.string,
      geojson: PropTypes.shape({
        type: PropTypes.string,
        coordinates: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.arrayOf(
            PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
          ),
        ]),
      }),
    })
  ).isRequired,
  data: PropTypes.shape({
    listingSearch: PropTypes.shape({
      listings: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            .isRequired,
          squareFeet: PropTypes.number,
          floorAndSuite: PropTypes.string,
          estimatedMonthlyPrice: PropTypes.number,
          accurateMonthlyPrice: PropTypes.number,
          coverImage: PropTypes.shape({
            id: PropTypes.string,
            url: PropTypes.string,
          }),
          sublease: PropTypes.bool,
          leaseTerm: PropTypes.string,
          leaseType: PropTypes.string,
          description: PropTypes.string,
          estimatedPsf: PropTypes.number,
          accuratePsf: PropTypes.number,
          building: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
              .isRequired,
            identity: PropTypes.string.isRequired,
            lat: PropTypes.number,
            lng: PropTypes.number,
            address: PropTypes.string,
            slug: PropTypes.string,
            classification: PropTypes.string,
            market: PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
            submarkets: PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
            neighborhood: PropTypes.shape({
              id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
              name: PropTypes.string,
              parameterizedName: PropTypes.string,
              cmsContent: PropTypes.shape({
                content: PropTypes.string,
                image: PropTypes.string,
                link: PropTypes.string,
                title: PropTypes.string,
              }),
            }),
          }),
          brokerInsights: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            })
          ),
        })
      ),
      count: PropTypes.number,
      offset: PropTypes.number,
    }),
    city: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      market: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        position: PropTypes.shape({
          lat: PropTypes.number,
          lng: PropTypes.number,
        }),
      }),
    }),
    cmsContent: PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string,
    }),
  }),
};

PublicSearchPage.defaultProps = {
  sanitizedCMS: null,
  numEmployees: null,
  data: null,
};
